import React, { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { getLongTasksMetrics } from '@atlassian/jira-common-long-task-metrics/src/index.tsx';
import { softwareGenericLongTasksReporter } from '@atlassian/jira-software-browser-metrics/src/services/reporters/software-generic-long-tasks/index.tsx';
import { useEvent } from '@atlassian/jira-software-react-use-event/src/index.tsx';
import {
	getDetailViewWidthRatio,
	setDetailViewWidthRatio,
} from '../../../services/local-storage/index.tsx';

type Props = {
	parentWidth: number;
	children: ReactNode;
	setWidth?: number | undefined;
	onResize?: ((arg1: number) => void) | undefined;
	onResizeEnd?: (ratio: number) => void;
};

export const MIN_DETAILS_WIDTH_ISSUE = 495;
export const MIN_DETAILS_WIDTH_INSIGHTS = 440;

export function Resizer({
	setWidth: widthProp = undefined,
	onResize = undefined,
	children,
	parentWidth,
	onResizeEnd,
}: Props) {
	const [width, setWidth] = useState(() => {
		const initValue = getDetailViewWidthRatio();
		return Math.max(parentWidth * initValue, MIN_DETAILS_WIDTH_ISSUE);
	});
	const startX = useRef(0);
	const extraWidth = useRef(0);

	useEffect(() => {
		const initValue = getDetailViewWidthRatio();
		setWidth(Math.max(parentWidth * initValue, MIN_DETAILS_WIDTH_ISSUE));
	}, [parentWidth]);

	useEffect(() => {
		onResize?.(widthProp !== undefined && widthProp !== 0 ? widthProp : width);
	}, [onResize, width, widthProp]);

	const onMouseUp = useEvent(() => {
		startX.current = 0;

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.removeEventListener('mousemove', onMouseMove);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.removeEventListener('mouseup', onMouseUp);

		const widthRatio = width === MIN_DETAILS_WIDTH_ISSUE ? 0 : width / parentWidth;
		setDetailViewWidthRatio(widthRatio);
		getLongTasksMetrics('detail-view-resizer').stop(
			softwareGenericLongTasksReporter('detail-view-resizer'),
		);
		onResizeEnd && onResizeEnd(width / parentWidth);
	});

	const onMouseDown = useEvent((e: React.MouseEvent) => {
		startX.current = e.clientX + (width - MIN_DETAILS_WIDTH_ISSUE);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('mousemove', onMouseMove);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('mouseup', onMouseUp);

		getLongTasksMetrics('detail-view-resizer').start('jira-backlog-detail-view-resizer');
	});

	const onMouseMove = useEvent((e: MouseEvent) => {
		extraWidth.current = Math.max(startX.current - e.clientX, 0);
		const newValue = Math.min(MIN_DETAILS_WIDTH_ISSUE + extraWidth.current, parentWidth / 2);

		if (newValue === width) {
			return;
		}

		setWidth(newValue);
	});

	return (
		<Box
			xcss={containerStyle}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{ width: widthProp != null && widthProp !== 0 ? widthProp : width }}
		>
			<Box
				xcss={resizeHandleStyle}
				onMouseDown={onMouseDown}
				testId="software-backlog.detail-view.resizer.resize-handle"
			/>
			{children}
		</Box>
	);
}

const containerStyle = xcss({
	overflowY: 'auto',
	overflowX: 'hidden',
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'flex-start',
});

const resizeHandleStyle = xcss({
	width: token('space.100'),
	cursor: 'ew-resize',
	borderLeftStyle: 'solid',
	borderLeftWidth: '2px',
	borderLeftColor: 'transparent',
	alignSelf: 'stretch',
	':hover': {
		borderLeftColor: 'color.border.focused',
	},
	':focus': {
		borderLeftColor: 'color.border.focused',
	},
	transition: 'border-color 0.5s',
});
